import { BASE_URL, DEFAULT_TIMEOUT, MAX_RETRY_ATTEMPTS, RETRY_DELAY_MS } from './constants.js';
import { MessengerAPIError, MessengerNetworkError, MessengerTimeoutError } from './errors.js';
import type { ErrorResponse } from '../types/responses.js';

export interface ClientConfig {
  accessToken?: string;
  version: string;
  baseUrl?: string;
  timeout?: number;
  maxRetries?: number;
}

export interface APIOptions {
  accessToken?: string;
}

export interface RequestOptions {
  method: 'GET' | 'POST' | 'DELETE';
  path: string;
  body?: any;
  query?: Record<string, string | number | boolean>;
  accessToken?: string;
}

export class HTTPClient {
  private readonly config: Required<Omit<ClientConfig, 'accessToken'>> & { accessToken?: string };

  constructor(config: ClientConfig) {
    this.config = {
      accessToken: config.accessToken,
      version: config.version,
      baseUrl: config.baseUrl || BASE_URL,
      timeout: config.timeout || DEFAULT_TIMEOUT,
      maxRetries: config.maxRetries || MAX_RETRY_ATTEMPTS,
    };
  }

  async request<T>(options: RequestOptions): Promise<T> {
    const url = this.buildUrl(options.path, options.query, options.accessToken);
    let lastError: Error | undefined;

    for (let attempt = 0; attempt <= this.config.maxRetries; attempt++) {
      try {
        const response = await this.makeRequest(url, options);
        return await this.handleResponse<T>(response);
      } catch (error) {
        lastError = error as Error;

        // Don't retry on client errors (4xx)
        if (
          error instanceof MessengerAPIError &&
          error.statusCode >= 400 &&
          error.statusCode < 500
        ) {
          throw error;
        }

        // Don't retry on timeout for the last attempt
        if (attempt === this.config.maxRetries) {
          throw error;
        }

        // Wait before retrying
        await this.delay(RETRY_DELAY_MS * (attempt + 1));
      }
    }

    throw lastError || new Error('Unknown error occurred');
  }

  private buildUrl(
    path: string,
    query?: Record<string, string | number | boolean>,
    accessTokenOverride?: string,
  ): string {
    const url = new URL(`${this.config.baseUrl}/${this.config.version}${path}`);

    // Use override token if provided, otherwise use config token
    const accessToken = accessTokenOverride || this.config.accessToken;

    if (!accessToken) {
      throw new Error('Access token is required. Provide it in constructor or method options.');
    }

    url.searchParams.append('access_token', accessToken);

    // Add additional query parameters
    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        url.searchParams.append(key, String(value));
      });
    }

    return url.toString();
  }

  private async makeRequest(url: string, options: RequestOptions): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

    try {
      const fetchOptions: RequestInit = {
        method: options.method,
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      };

      if (options.body) {
        fetchOptions.body = JSON.stringify(options.body);
      }

      const response = await fetch(url, fetchOptions);
      return response;
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new MessengerTimeoutError(this.config.timeout);
        }
        throw new MessengerNetworkError(`Network request failed: ${error.message}`, error);
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type');
    const isJson = contentType?.includes('application/json');

    if (!response.ok) {
      if (isJson) {
        const errorData = (await response.json()) as ErrorResponse;
        throw new MessengerAPIError(errorData.error, response.status, errorData);
      } else {
        const text = await response.text();
        throw new MessengerAPIError(
          {
            message: text || `HTTP ${response.status} ${response.statusText}`,
            type: 'http_error',
            code: response.status,
            fbtrace_id: '',
          },
          response.status,
          text,
        );
      }
    }

    if (isJson) {
      return (await response.json()) as T;
    }

    // For non-JSON responses, return the text
    return (await response.text()) as unknown as T;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
